<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  activeFilters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:filters', 'clear']);

const { t } = useI18n();
const isOpen = ref(false);

const companyTypes = [
  { key: 'prospect', label: 'Prospect' },
  { key: 'customer', label: 'Customer' },
  { key: 'partner', label: 'Partner' },
  { key: 'competitor', label: 'Competitor' },
  { key: 'vendor', label: 'Vendor' },
];

const companyStatuses = [
  { key: 'active', label: 'Active' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'churned', label: 'Churned' },
];

const industries = [
  { key: 'technology', label: 'Technology' },
  { key: 'healthcare', label: 'Healthcare' },
  { key: 'finance', label: 'Finance' },
  { key: 'retail', label: 'Retail' },
  { key: 'manufacturing', label: 'Manufacturing' },
  { key: 'education', label: 'Education' },
  { key: 'real_estate', label: 'Real Estate' },
  { key: 'services', label: 'Services' },
  { key: 'other', label: 'Other' },
];

const hasActiveFilters = computed(() => {
  return Object.values(props.activeFilters).some(v => v && v.length > 0);
});

const activeFilterCount = computed(() => {
  return Object.values(props.activeFilters).filter(v => v && v.length > 0)
    .length;
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

const toggleFilter = (filterType, value) => {
  const currentFilters = { ...props.activeFilters };

  if (!currentFilters[filterType]) {
    currentFilters[filterType] = [];
  }

  const index = currentFilters[filterType].indexOf(value);
  if (index === -1) {
    currentFilters[filterType] = [...currentFilters[filterType], value];
  } else {
    currentFilters[filterType] = currentFilters[filterType].filter(
      v => v !== value
    );
  }

  emit('update:filters', currentFilters);
};

const isFilterActive = (filterType, value) => {
  return props.activeFilters[filterType]?.includes(value) || false;
};

const clearAllFilters = () => {
  emit('clear');
  closeMenu();
};
</script>

<template>
  <div v-on-click-outside="closeMenu" class="relative">
    <Button
      icon="i-lucide-list-filter"
      color="slate"
      size="sm"
      class="relative w-8"
      variant="ghost"
      @click="toggleOpen"
    >
      <div
        v-if="hasActiveFilters"
        class="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-medium rounded-full bg-n-brand text-white"
      >
        {{ activeFilterCount }}
      </div>
    </Button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute right-0 z-50 mt-2 w-72 origin-top-right rounded-xl bg-n-solid-2 shadow-lg ring-1 ring-n-container overflow-hidden"
      >
        <div class="p-3 border-b border-n-weak">
          <div class="flex items-center justify-between">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('COMPANIES_FILTERS.TITLE') }}
            </span>
            <Button
              v-if="hasActiveFilters"
              size="xs"
              variant="ghost"
              color="slate"
              :label="t('COMPANIES_FILTERS.CLEAR_ALL')"
              @click="clearAllFilters"
            />
          </div>
        </div>

        <div class="p-3 space-y-4 max-h-80 overflow-y-auto">
          <!-- Company Type Filter -->
          <div>
            <span
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ t('COMPANIES_FILTERS.COMPANY_TYPE') }}
            </span>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="type in companyTypes"
                :key="type.key"
                class="px-2.5 py-1 text-xs rounded-md transition-colors"
                :class="[
                  isFilterActive('company_type', type.key)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
                ]"
                @click="toggleFilter('company_type', type.key)"
              >
                {{ type.label }}
              </button>
            </div>
          </div>

          <!-- Status Filter -->
          <div>
            <span
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ t('COMPANIES_FILTERS.STATUS') }}
            </span>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="status in companyStatuses"
                :key="status.key"
                class="px-2.5 py-1 text-xs rounded-md transition-colors"
                :class="[
                  isFilterActive('status', status.key)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
                ]"
                @click="toggleFilter('status', status.key)"
              >
                {{ status.label }}
              </button>
            </div>
          </div>

          <!-- Industry Filter -->
          <div>
            <span
              class="text-xs font-medium text-n-slate-11 uppercase tracking-wide"
            >
              {{ t('COMPANIES_FILTERS.INDUSTRY') }}
            </span>
            <div class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="industry in industries"
                :key="industry.key"
                class="px-2.5 py-1 text-xs rounded-md transition-colors"
                :class="[
                  isFilterActive('industry', industry.key)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3',
                ]"
                @click="toggleFilter('industry', industry.key)"
              >
                {{ industry.label }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
